<template>
    <div>
        <div v-if='imageLoadError === false'>
            <p class='text-14 text-grey-900'>Dimensions {{ canvasDims.width }} x {{ canvasDims.height }}</p>
            <p class='text-14 text-grey-900'>Image original dimensions {{originalWidth }}px x {{ originalHeight }}px</p>

            <!-- <InputCheckbox class='mt-4' @change="viewScale == 200 ? viewScale = 100 : viewScale = 200" :checked="viewScale == 200">
                <p class='text-14 text-grey-900 pl-2 leading-[20px]'>View at 200% scale</p>
            </InputCheckbox> -->

            <!-- {{ image.original_upload }} -->

            <input type='hidden' name='edit_options' :value='JSON.stringify({
                "scale": scale,
                "position": imgPosition,
                "exposure": exposure,
                "brightness": brightness
            })' />

            <input v-if='$refs.canvas' type="hidden" name="image" :value="$refs.canvas.toDataURL('image/png')" />

            <canvas ref="canvas" class='border border-grey-500 mt-4 bg-grey-300 box-content' :style='canvasStyle' @mousedown="handleMouseDown" @mousemove="handleMouseMove" @mouseup="handleMouseUp" @mouseleave="handleMouseUp" />

            <div class='flex items-center gap-4 mt-6'>
                <p class='text-14 text-grey-900 w-[70px] shrink-0'>Scale</p>

                <input type="range" min="0.01" max="1" step="0.01" 
                    v-model="scale" @input="updateScale" 
                    class="w-full bg-grey-300 h-[2px]" 
                />

                <p class='text-14 text-grey-900 w-[40px] text-right shrink-0'>{{ parseFloat((scale) * 100).toFixed(0) }}%</p>

                <button type='button' @click="resetScale">
                    <IconRefresh class='icon-hover w-4.5 h-4.5' />
                </button>
            </div>

            <div class='flex items-center gap-4 mt-4'>
                <p class='text-14 text-grey-900 w-[70px] shrink-0'>Exposure</p>

                <input type="range" min="0" max="2" step="0.01" 
                    v-model="exposure" @input="updateExposure" 
                    class="w-full bg-grey-300 h-[2px]" 
                />

                <p class='text-14 text-grey-900 w-[40px] text-right shrink-0'>{{ (parseFloat(exposure-1) * 100).toFixed(0) }}%</p>

                <button type='button' @click="resetExposure">
                    <IconRefresh class='icon-hover w-4.5 h-4.5' />
                </button>
            </div>

            <div class='flex items-center gap-4 mt-4'>
                <p class='text-14 text-grey-900 w-[70px] shrink-0'>Brightness</p>
                <input
                    type="range"
                    min="-1"
                    max="1"
                    step="0.01"
                    v-model="brightness"
                    @input="updateBrightness"
                    class="w-full bg-grey-300 h-[2px]"
                />
                <p class='text-14 text-grey-900 w-[40px] text-right shrink-0'>{{ (parseFloat(brightness) * 100).toFixed(0) }}%</p>
                <button type='button' @click="resetBrightness">
                    <IconRefresh class='icon-hover w-4.5 h-4.5' />
                </button>
            </div>
        </div>
        
        <div class='bg-red-700/20 p-4 text-red-700 flex' v-else>
            <IconInfo class='w-4.5 h-4.5 stroke-red-700 mr-2 mt-0.5' />
            <p class='text-15'>This image could not be loaded: {{ imageLoadError }}</p>
        </div>
    </div>
</template>

<script>
    export default {
        props: {
            image: Object,
            tag: Object
        }, 
        
        data() {
            return {
                imageLoadError: false,
                scale: 1,
                exposure: 1,
                brightness: 0,
                isDragging: false,
                dragStart: { x: 0, y: 0 },
                imgPosition: { x: 0, y: 0 },
                originalWidth: 0,
                originalHeight: 0,
                gl: null,
                program: null,
                positionLocation: null,
                texCoordLocation: null,
                positionBuffer: null,
                texCoordBuffer: null,
                resolutionLocation: null,
                scaleLocation: null,
                translationLocation: null,
                brightnessLocation: null,
                viewScale: 100
            };
        },

        mounted() {
            if (this.image.original_upload && this.image.edit_options != null) {
                let edit_options = JSON.parse(this.image.edit_options);
                this.scale = edit_options.scale;
                this.imgPosition = edit_options.position;
                this.exposure = edit_options.exposure;
                this.brightness = edit_options.brightness;

                // this.drawScene();
            }

            this.initWebGL();
        },

        methods: {
            initWebGL() {
                this.imageLoadError = false;

                const canvas = this.$refs.canvas;
                // Set the canvas's drawing buffer dimensions from the computed CSS dimensions.
                canvas.width = parseInt(this.canvasDims.width) * this.canvasDims.scale;
                canvas.height = parseInt(this.canvasDims.height) * this.canvasDims.scale;
                
                // Request a WebGL context with the preserveDrawingBuffer flag enabled.
                const gl = canvas.getContext('webgl', {
                    preserveDrawingBuffer: true,
                });
                if (!gl) {
                    console.error('WebGL not supported');
                    return;
                }
                this.gl = gl;

                // Vertex shader: It receives image pixel coordinates and applies scale and translation,
                // then converts them to clip space using the canvas resolution.
                const vertexShaderSource = `
                    attribute vec2 a_position;
                    attribute vec2 a_texCoord;
                    uniform vec2 u_resolution;
                    uniform float u_scale;
                    uniform vec2 u_translation;
                    varying vec2 v_texCoord;
                    void main() {
                        // Scale the image pixel coordinates.
                        vec2 scaledPosition = a_position * u_scale;
                        // Add user translation (in pixel space).
                        vec2 pos = scaledPosition + u_translation;
                        // Convert from pixel space to clip space.
                        vec2 clipSpace = vec2(
                            (pos.x / u_resolution.x) * 2.0 - 1.0,
                            1.0 - (pos.y / u_resolution.y) * 2.0
                        );
                        gl_Position = vec4(clipSpace, 0, 1);
                        v_texCoord = a_texCoord;
                    }
                `;
                const fragmentShaderSource = `
                    precision mediump float;
                    uniform sampler2D u_image;
                    uniform float u_exposure;
                    uniform float u_brightness;
                    varying vec2 v_texCoord;
                    void main() {
                        vec4 color = texture2D(u_image, v_texCoord);
                        gl_FragColor = vec4((color.rgb * u_exposure + u_brightness), color.a);
                    }
                `;
                const vertexShader = this.createShader(gl, gl.VERTEX_SHADER, vertexShaderSource);
                const fragmentShader = this.createShader(gl, gl.FRAGMENT_SHADER, fragmentShaderSource);
                const program = this.createProgram(gl, vertexShader, fragmentShader);
                this.program = program;
                gl.useProgram(program);

                // Get attribute locations.
                const positionLocation = gl.getAttribLocation(program, "a_position");
                const texCoordLocation = gl.getAttribLocation(program, "a_texCoord");
                this.positionLocation = positionLocation;
                this.texCoordLocation = texCoordLocation;

                // Create buffers.
                const positionBuffer = gl.createBuffer();
                this.positionBuffer = positionBuffer;
                const texCoordBuffer = gl.createBuffer();
                this.texCoordBuffer = texCoordBuffer;

                // We'll load the image first then fill the position buffer with a rectangle using pixel coordinates.
                gl.bindBuffer(gl.ARRAY_BUFFER, texCoordBuffer);
                gl.bufferData(
                    gl.ARRAY_BUFFER,
                    new Float32Array([
                        0, 1, // bottom-left
                        1, 1, // bottom-right
                        0, 0, // top-left
                        0, 0, // top-left
                        1, 1, // bottom-right
                        1, 0  // top-right
                    ]),
                    gl.STATIC_DRAW
                );

                // Create a texture.
                const texture = gl.createTexture();
                gl.bindTexture(gl.TEXTURE_2D, texture);
                gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
                gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
                gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
                gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);

                // Load the image
                const image = new Image();

                console.log(this.image)
                // console.log(this.image.original_upload.id, this.image.original_upload.url)
                
                image.crossOrigin = "anonymous";

                try {
                    image.src = this.image.original_upload ? this.image.original_upload.url : this.image.url;
                } catch (error) {
                    this.imageLoadError = error;
                }

                if (this.image.url == null) {
                    image.src = useRuntimeConfig().public.landingPageURL + this.image.file_name.replace("public", "/storage")
                }


                // image.src = this.image.original_upload.url
                image.onload = () => {
                    // Save the image's original dimensions.
                    this.originalWidth = image.width;
                    this.originalHeight = image.height;

                    // Create a rectangle that matches the image's pixel size.
                    const positions = new Float32Array([
                        0, 0,
                        image.width * this.canvasDims.scale, 0,
                        0, image.height * this.canvasDims.scale,
                        0, image.height * this.canvasDims.scale,
                        image.width * this.canvasDims.scale, 0,
                        image.width * this.canvasDims.scale, image.height * this.canvasDims.scale
                    ]);
                    gl.bindBuffer(gl.ARRAY_BUFFER, positionBuffer);
                    gl.bufferData(gl.ARRAY_BUFFER, positions, gl.STATIC_DRAW);

                    // Flip the image vertically so it doesn't appear upside down.
                    gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, true);

                    // Upload the image into the texture.
                    gl.bindTexture(gl.TEXTURE_2D, texture);
                    gl.texImage2D(
                        gl.TEXTURE_2D,
                        0,
                        gl.RGBA,
                        gl.RGBA,
                        gl.UNSIGNED_BYTE,
                        image
                    );
                    gl.generateMipmap(gl.TEXTURE_2D);

                    // Get uniform locations for our new uniforms.
                    this.resolutionLocation = gl.getUniformLocation(
                        program,
                        "u_resolution"
                    );
                    this.scaleLocation = gl.getUniformLocation(program, "u_scale");
                    this.translationLocation = gl.getUniformLocation(
                        program,
                        "u_translation"
                    );
                    this.brightnessLocation = gl.getUniformLocation(program, "u_brightness");

                    // Finally, draw the scene.
                    this.drawScene();
                };
            },

            createShader(gl, type, source) {
                const shader = gl.createShader(type);
                gl.shaderSource(shader, source);
                gl.compileShader(shader);
                const success = gl.getShaderParameter(shader, gl.COMPILE_STATUS);
                if (success) {
                    return shader;
                }
                console.error(gl.getShaderInfoLog(shader));
                gl.deleteShader(shader);
            },

            createProgram(gl, vertexShader, fragmentShader) {
                const program = gl.createProgram();
                gl.attachShader(program, vertexShader);
                gl.attachShader(program, fragmentShader);
                gl.linkProgram(program);
                const success = gl.getProgramParameter(program, gl.LINK_STATUS);
                if (success) {
                    return program;
                }
                console.error(gl.getProgramInfoLog(program));
                gl.deleteProgram(program);
            },

            drawScene() {
                const gl = this.gl;
                gl.viewport(0, 0, gl.canvas.width, gl.canvas.height);
                gl.clearColor(0, 0, 0, 0);
                gl.clear(gl.COLOR_BUFFER_BIT);

                gl.useProgram(this.program);

                // Bind and set up the position attribute.
                gl.bindBuffer(gl.ARRAY_BUFFER, this.positionBuffer);
                gl.enableVertexAttribArray(this.positionLocation);
                gl.vertexAttribPointer(this.positionLocation, 2, gl.FLOAT, false, 0, 0);

                // Bind and set up the texture coordinate attribute.
                gl.bindBuffer(gl.ARRAY_BUFFER, this.texCoordBuffer);
                gl.enableVertexAttribArray(this.texCoordLocation);
                gl.vertexAttribPointer(this.texCoordLocation, 2, gl.FLOAT, false, 0, 0);

                // Set the uniforms.
                gl.uniform2f(
                    this.resolutionLocation,
                    gl.canvas.width,
                    gl.canvas.height
                );
                gl.uniform1f(this.scaleLocation, this.scale);
                gl.uniform2f(
                    this.translationLocation,
                    this.imgPosition.x,
                    this.imgPosition.y
                );

                // Set the exposure uniform.
                const exposureLocation = gl.getUniformLocation(
                    this.program,
                    "u_exposure"
                );
                gl.uniform1f(exposureLocation, this.exposure);

                // Set the brightness uniform.
                gl.uniform1f(this.brightnessLocation, this.brightness);

                // Draw the image. Portions outside the canvas will naturally be clipped.
                gl.drawArrays(gl.TRIANGLES, 0, 6);
            },

            updateExposure() {
                if (this.gl) {
                    this.drawScene();
                }
            },

            updateScale() {
                if (this.gl) {
                    this.drawScene();
                }
            },

            updateBrightness() {
                if (this.gl) {
                    this.drawScene();
                }
            },

            resetScale() {
                this.scale = 1;
                if (this.gl) {
                    this.drawScene();
                }
            },

            resetExposure() {
                this.exposure = 1;
                if (this.gl) {
                    this.drawScene();
                }
            },

            resetBrightness() {
                this.brightness = 0;
                if (this.gl) {
                    this.drawScene();
                }
            },

            // Mouse event handlers for dragging the image.
            handleMouseDown(event) {
                this.isDragging = true;
                // Record the relative starting point.
                // event.offsetX and event.offsetY are the coordinates within the canvas.
                this.dragStart.x = event.offsetX - this.imgPosition.x;
                this.dragStart.y = event.offsetY - this.imgPosition.y;
            },

            handleMouseMove(event) {
                if (!this.isDragging) return;
                // Update the image position based on the drag.
                this.imgPosition.x = event.offsetX - this.dragStart.x;
                this.imgPosition.y = event.offsetY - this.dragStart.y;
                // Redraw the scene with the updated position.
                this.drawScene();
            },

            handleMouseUp() {
                this.isDragging = false;
            }
        },

        computed: {
            canvasDims() {
                if (! this?.tag?.tag?.tag) {
                    return { width: '800px', height: '500px', scale: 1 }
                }

                switch (this.tag.tag.tag) {
                    case 'gallery':
                        return { width: '800px', height: '500px', scale: 1 }
                    case '[ImageSplashLong]':
                        return { width: '1920px', height: '350px', scale: 0.5 }
                    default:
                        return { width: 600 * this.viewScale / 100 +'px', height: 400 * this.viewScale / 100 +'px', scale: 1 }
                }
            },
            canvasStyle() {
                const dims = this.canvasDims;
                return {
                    width: parseInt(dims.width) * dims.scale + 'px',
                    height: parseInt(dims.height) * dims.scale + 'px'
                };
            }
        }
    }
</script>